import { Link } from 'react-router-dom';
import {
    Dumbbell,
    Users,
    CreditCard,
    MessageCircle,
    TrendingUp,
    Clock,
    CheckCircle,
    Star,
    ArrowRight,
    Zap,
    Shield,
    Smartphone,
} from 'lucide-react';

export function LandingPage() {
    const scrollToSection = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950">
            {/* Fixed Header - Clean & Minimal */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-950/90 backdrop-blur-xl border-b border-gray-100 dark:border-gray-900">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <img src="/gofit-logo.png" alt="GoFit" className="w-10 h-10" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                GoFit
                            </span>
                        </div>
                        <nav className="hidden md:flex items-center gap-8">
                            <button onClick={() => scrollToSection('recursos')} className="text-gray-500 hover:text-primary-600 transition-colors text-sm font-medium">
                                Recursos
                            </button>
                            <button onClick={() => scrollToSection('beneficios')} className="text-gray-500 hover:text-primary-600 transition-colors text-sm font-medium">
                                Benefícios
                            </button>
                            <button onClick={() => scrollToSection('planos')} className="text-gray-500 hover:text-primary-600 transition-colors text-sm font-medium">
                                Planos
                            </button>
                        </nav>
                        <div className="flex items-center gap-3">
                            <Link to="/login" className="px-4 py-2 text-gray-600 dark:text-gray-300 text-sm font-medium hover:text-primary-600 transition-colors">
                                Entrar
                            </Link>
                            <Link to="/signup" className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-primary-500/25 transition-all">
                                Começar Grátis
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section - Elegant & Spacious */}
            <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-50 dark:bg-primary-950/50 rounded-full mb-8">
                        <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                            Plataforma #1 para Personal Trainers
                        </span>
                    </div>

                    {/* Main Headline */}
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-[1.1] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Gerencie seus alunos
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500">
                            com elegância e eficiência
                        </span>
                    </h1>

                    {/* Subheadline */}
                    <p className="text-lg sm:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Treinos personalizados, cobranças automáticas e evolução em tempo real.
                        Tudo em uma plataforma simples e poderosa.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
                        <Link
                            to="/signup"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-primary-500/30 transition-all hover:-translate-y-0.5"
                        >
                            Começar Gratuitamente
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <button
                            onClick={() => scrollToSection('recursos')}
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-medium rounded-full border border-gray-200 dark:border-gray-800 hover:border-primary-300 transition-all"
                        >
                            Conhecer Recursos
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="flex flex-wrap justify-center gap-8 sm:gap-16">
                        <div className="text-center">
                            <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">500+</p>
                            <p className="text-sm text-gray-500 mt-1">Personal Trainers</p>
                        </div>
                        <div className="text-center">
                            <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">10k+</p>
                            <p className="text-sm text-gray-500 mt-1">Alunos Ativos</p>
                        </div>
                        <div className="text-center">
                            <p className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">98%</p>
                            <p className="text-sm text-gray-500 mt-1">Satisfação</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section - Clean Cards */}
            <section id="recursos" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900/50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Tudo que você precisa, em um só lugar
                        </h2>
                        <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                            Ferramentas profissionais para elevar seu negócio ao próximo nível
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Dumbbell,
                                title: 'Criador de Treinos',
                                description: 'Monte treinos personalizados com blocos e templates prontos. Biblioteca com +500 exercícios.',
                                color: 'bg-primary-500',
                            },
                            {
                                icon: Users,
                                title: 'Gestão de Alunos',
                                description: 'Perfil completo, histórico de treinos e evolução. Gerencie até alunos sem app.',
                                color: 'bg-accent-500',
                            },
                            {
                                icon: CreditCard,
                                title: 'Cobranças Automáticas',
                                description: 'PIX, boleto e cartão. Lembretes automáticos e controle de inadimplência.',
                                color: 'bg-green-500',
                            },
                            {
                                icon: TrendingUp,
                                title: 'Relatórios de Evolução',
                                description: 'Gráficos de peso, medidas e volume de treino. Acompanhe o progresso em tempo real.',
                                color: 'bg-blue-500',
                            },
                            {
                                icon: MessageCircle,
                                title: 'WhatsApp Integrado',
                                description: 'Envie treinos, lembretes e comunique-se de forma profissional.',
                                color: 'bg-emerald-500',
                            },
                            {
                                icon: Smartphone,
                                title: 'Bio Page Profissional',
                                description: 'Página de apresentação com link único para redes sociais e captação.',
                                color: 'bg-pink-500',
                            },
                        ].map((feature, i) => (
                            <div
                                key={i}
                                className="group bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-800 hover:border-primary-200 dark:hover:border-primary-800 transition-all hover:shadow-lg hover:-translate-y-1"
                            >
                                <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                    <feature.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section - Alternating Layout */}
            <section id="beneficios" className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Por que escolher o GoFit?
                        </h2>
                        <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                            Feito por personal trainers, para personal trainers
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {[
                            {
                                icon: Clock,
                                title: 'Economize 10h por semana',
                                description: 'Automatize tarefas repetitivas como cobranças, lembretes e envio de treinos.',
                            },
                            {
                                icon: Shield,
                                title: 'Dados seguros',
                                description: 'Seus dados e de seus alunos protegidos com criptografia de ponta.',
                            },
                            {
                                icon: Zap,
                                title: 'Fácil de usar',
                                description: 'Interface intuitiva. Comece a usar em menos de 5 minutos.',
                            },
                            {
                                icon: TrendingUp,
                                title: 'Escale seu negócio',
                                description: 'Atenda mais alunos sem perder qualidade no acompanhamento.',
                            },
                        ].map((benefit, i) => (
                            <div key={i} className="flex gap-4 p-6 rounded-2xl bg-gray-50 dark:bg-gray-900/50">
                                <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                                        <benefit.icon className="w-6 h-6 text-white" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                                        {benefit.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials - Simple Cards */}
            <section id="depoimentos" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900/50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            O que dizem nossos clientes
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                name: 'Lucas Mendes',
                                role: 'Personal Trainer',
                                text: 'Consegui dobrar minha carteira de alunos em 3 meses. O GoFit me deu tempo para focar no que importa: treinar!',
                                rating: 5,
                            },
                            {
                                name: 'Amanda Santos',
                                role: 'Personal Premium',
                                text: 'A cobrança automática acabou com a inadimplência. Não preciso mais cobrar ninguém manualmente.',
                                rating: 5,
                            },
                            {
                                name: 'Rafael Costa',
                                role: 'Dono de Academia',
                                text: 'Interface limpa e profissional. Meus alunos adoram acompanhar a evolução pelo app.',
                                rating: 5,
                            },
                        ].map((testimonial, i) => (
                            <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-800">
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, j) => (
                                        <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                                <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm leading-relaxed">
                                    "{testimonial.text}"
                                </p>
                                <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">{testimonial.name}</p>
                                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section - Clean Cards */}
            <section id="planos" className="py-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Planos simples e transparentes
                        </h2>
                        <p className="text-lg text-gray-500 dark:text-gray-400">
                            Comece grátis. Upgrade quando precisar.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Free Plan */}
                        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Free</h3>
                            <p className="text-gray-500 text-sm mb-4">Para começar</p>
                            <div className="mb-6">
                                <span className="text-4xl font-bold text-gray-900 dark:text-white">R$ 0</span>
                                <span className="text-gray-500">/mês</span>
                            </div>
                            <ul className="space-y-3 mb-6">
                                {['Até 5 alunos', 'Criador de treinos', 'App para alunos', 'Suporte por email'].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup" className="block w-full py-3 text-center border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:border-primary-500 hover:text-primary-600 transition-colors">
                                Criar Conta
                            </Link>
                        </div>

                        {/* Pro Plan - Highlighted */}
                        <div className="bg-gradient-to-b from-primary-600 to-primary-700 rounded-2xl p-6 text-white relative">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
                                POPULAR
                            </div>
                            <h3 className="text-lg font-semibold mb-2">Pro</h3>
                            <p className="text-primary-100 text-sm mb-4">Para profissionais</p>
                            <div className="mb-6">
                                <span className="text-4xl font-bold">R$ 89,90</span>
                                <span className="text-primary-200">/mês</span>
                            </div>
                            <ul className="space-y-3 mb-6">
                                {['Até 50 alunos', 'Cobranças automáticas', 'WhatsApp integrado', 'Bio page profissional', 'Relatórios avançados', 'Suporte prioritário'].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-primary-100">
                                        <CheckCircle className="w-4 h-4 text-primary-200 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup?plan=pro" className="block w-full py-3 text-center bg-white text-primary-700 font-semibold rounded-xl hover:bg-primary-50 transition-colors">
                                Começar Agora
                            </Link>
                        </div>

                        {/* Starter Plan */}
                        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Starter</h3>
                            <p className="text-gray-500 text-sm mb-4">Para crescer</p>
                            <div className="mb-6">
                                <span className="text-4xl font-bold text-gray-900 dark:text-white">R$ 59,90</span>
                                <span className="text-gray-500">/mês</span>
                            </div>
                            <ul className="space-y-3 mb-6">
                                {['Até 10 alunos', 'Cobranças automáticas', '50 msgs WhatsApp/mês', 'Bio page básica'].map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup?plan=starter" className="block w-full py-3 text-center border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:border-primary-500 hover:text-primary-600 transition-colors">
                                Começar
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Pronto para transformar seu negócio?
                    </h2>
                    <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
                        Junte-se a centenas de personal trainers que já estão crescendo com o GoFit.
                    </p>
                    <Link
                        to="/signup"
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-700 font-semibold rounded-full hover:shadow-xl transition-all hover:-translate-y-0.5"
                    >
                        Criar Minha Conta Grátis
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>

            {/* Footer - Minimal */}
            <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-100 dark:border-gray-900">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <img src="/gofit-logo.png" alt="GoFit" className="w-8 h-8" />
                            <span className="font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>GoFit</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                            <a href="#" className="hover:text-primary-600 transition-colors">Termos de Uso</a>
                            <a href="#" className="hover:text-primary-600 transition-colors">Privacidade</a>
                            <a href="#" className="hover:text-primary-600 transition-colors">Contato</a>
                            <Link to="/afiliado" className="hover:text-primary-600 transition-colors">Seja Afiliado</Link>
                        </div>
                        <p className="text-sm text-gray-400">
                            © 2024 GoFit. Todos os direitos reservados.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
